import React, { useCallback, useEffect } from 'react';
import {
  Form, Input, Modal, DatePicker, InputNumber,
} from 'antd';
import moment from 'moment';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import { selectEditState, selectSubmitBucketRequest, selectFetchBucketsRequest } from '../../../../state/ui/credits/bucketUiReducer';
import { cancelEditBucket, requestCreateBucket, requestUpdateBucket } from '../../../../state/ui/credits/bucketUiActions';
import {DATE_FORMAT, convertToUTCDate, getYearExpiry} from '../packageUtil';
import { selectBucket } from '../../../../state/domain/buckets/bucketReducer';

// FIELDS
/**
 * The field for the credit description.
 */
const DescriptionField = injectIntl(({
   form, defaultValue, intl, disabled,
 }) => (
    <Form.Item required label={<FormattedMessage id="description" />}>
      { form.getFieldDecorator('description', {
        initialValue: defaultValue,
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
          <Input
              disabled={disabled}
              type="text"
              maxLength={100}
              allowClear
              placeholder={intl.formatMessage({ id: 'enter' })}
          />,
      )}
    </Form.Item>
));

/**
 * The field for the from date
 */
const StartDateField = injectIntl(({
     form, creditBucket, intl, disabled, startDateDupeCheck,
    }) => (
    <Form.Item required label={<FormattedMessage id="startDateUTC" />}>
      { form.getFieldDecorator('dateFrom', {
        initialValue: creditBucket ? moment.utc(creditBucket.dateFrom) : moment.utc(),
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
          {
            validator(rule, value, callback) {
              // Cannot disable self
              if (!startDateDupeCheck(creditBucket ? creditBucket.id : null, convertToUTCDate(value))) {
                callback(intl.formatMessage({ id: 'duplicateBucket' }));
              }
              callback();
            },
          },
        ],
      })(
          <DatePicker
              style={{ width: '300px' }}
              disabled={disabled}
              disabledDate={value => convertToUTCDate(value).isBefore(moment.utc().startOf('day')) || convertToUTCDate(value).isAfter(moment.utc().add(7, 'days'))}
              format={DATE_FORMAT}
              placeholder={intl.formatMessage({ id: 'selectOne' })}
              showToday={true}
              allowClear={false}
              onChange={value => form.setFieldsValue({dateExpiry: getYearExpiry(value)})}
          />,
      )}
    </Form.Item>
));

/**
 * The field for the expiry date
 */
const EndDateField = injectIntl(({
       form, creditBucket, intl, disabled,
     }) => (
    <Form.Item required label={<FormattedMessage id="expiryDate" />}>
      { form.getFieldDecorator('dateExpiry', {
        initialValue: creditBucket ? moment.utc(creditBucket.dateExpiry) : (form.getFieldValue('dateFrom') ? getYearExpiry(form.getFieldValue('dateFrom')) : undefined),
        rules: [
            { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
            {
                validator(rule, value, callback) {
                    if (moment.utc(value) < moment.utc(form.getFieldValue('dateFrom'))) {
                        callback(intl.formatMessage({ id: 'expiryDateAfterStart' }));
                    }
                    callback();
                },
            },
        ],
      })(
          <DatePicker
              style={{ width: '300px' }}
              disabled={disabled}
              disabledDate={value => convertToUTCDate(value).isBefore(moment.utc())}
              format={DATE_FORMAT}
              placeholder={intl.formatMessage({ id: 'selectOne' })}
              showToday={false}
              allowClear={false}
          />,
      )}
    </Form.Item>
));

/**
 * The field for the tokens
 */
const AllocatedTokensField = injectIntl(({
       form, defaultValue, disabled, intl,
     }) => (
    <Form.Item required label={<FormattedMessage id="numberOfCredits" />}>
      { form.getFieldDecorator('tokensAllocated', {
        initialValue: defaultValue,
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
          <InputNumber
              disabled={disabled}
              precision={0}
              min={1}
              placeholder={intl.formatMessage({ id: 'enter' })}
          />,
      )}
    </Form.Item>
));

// MODAL AND FORM
const EditCreditBucket = injectIntl(Form.create({ name: 'edit-bucket' })(({
    intl, form, visible, setVisible, creditBucket, onCreate, onUpdate, usernameValidator, isLastAdmin, sessionUser,
    onCancel, isLoading, isSubmitting, validationBox, startDateDupeCheck, manageCompany, ...rest
}) => {
  const isCreating = !creditBucket;
  const titleId = isCreating ? 'addCreditBatch' : 'edit';  //edit not yet supported
  const buttonId = isCreating ? 'create' : 'save';

  const canEditAll = isCreating || moment.utc().isBefore(moment.utc(creditBucket.expiryDate));

  const { resetFields } = form;

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) {
      resetFields();
    }
  }, [resetFields, visible]);
  // On form submit.
  const submitForm = useCallback(() => {
    if (!canEditAll) { onCancel(); return; }
    form.validateFields();
    form.validateFields((err, values) => {
      if (!err) {
        const newBucket = creditBucket
            ? {
              //update - not yet coded for in API as currently not needed
              id: creditBucket.id,
              ...values,
              dateFrom: convertToUTCDate(values.dateFrom).startOf('day').toISOString(),
              dateExpiry: convertToUTCDate(values.dateExpiry).startOf('day').toISOString(),
              createdDate: convertToUTCDate(values.createdDate).startOf('day').toISOString(),
            }
            : {
              // create
              ...values,
              createdBy: sessionUser != null && sessionUser.userId > 0 ? sessionUser.userId : 0,
              createdUsername: sessionUser != null && sessionUser.userId > 0 ? sessionUser.username : '',   //for list
              dateFrom: convertToUTCDate(values.dateFrom).startOf('day').toISOString(),
              dateExpiry: convertToUTCDate(values.dateExpiry).startOf('day').toISOString(),
              createdDate: convertToUTCDate(moment()).startOf('day').toISOString(),
            };

        const submitFunction = isCreating ? onCreate : onUpdate;
        submitFunction(newBucket, manageCompany, intl);
      }
    });
  }, [form, isCreating, onCreate, onUpdate, intl, creditBucket, onCancel, canEditAll, manageCompany, sessionUser]);

  return (
      <Modal
          title={<FormattedMessage id={titleId} />}
          okText={<FormattedMessage id={buttonId} />}
          onOk={submitForm}
          visible={visible}
          onCancel={onCancel}
          confirmLoading={isSubmitting}
          {...rest}
      >
        <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
          <ViaValidationAlert validationError={validationBox} />
          <DescriptionField form={form} defaultValue={creditBucket ? creditBucket.description : null} disabled={!canEditAll} />
          <AllocatedTokensField form={form} defaultValue={creditBucket ? creditBucket.tokensAllocated : null} disabled={!canEditAll} />
          <StartDateField form={form} creditBucket={creditBucket} disabled={!canEditAll} startDateDupeCheck={startDateDupeCheck} />
          <EndDateField form={form} creditBucket={creditBucket} disabled={!canEditAll} />
        </Form>
      </Modal>
  );
}));

// Validate the prop type parameters.
EditCreditBucket.propTypes = {
  visible: PropTypes.bool.isRequired,
  sessionUser: PropTypes.shape({
    username: PropTypes.string.isRequired,
  }),
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  startDateDupeCheck: PropTypes.func.isRequired,
};

EditCreditBucket.defaultProps = {
  isLoading: false,
  validationBox: null,
};

const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const submitRequest = selectSubmitBucketRequest(state);
  const pageDataRequest = selectFetchBucketsRequest(state);

  return {
    visible: editState.isEditing,
    creditBucket: editState.code ? selectBucket(state, editState.code) : null,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    validationBox: submitRequest ? submitRequest.error : undefined,
  };
};

const mapDispatchToProps = dispatch => ({
  onCreate: (newBucket, manageCompany) => dispatch(requestCreateBucket(newBucket, manageCompany)),
  onUpdate: (newBucket) => dispatch(requestUpdateBucket(newBucket)),
  onCancel: () => dispatch(cancelEditBucket()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditCreditBucket);
